import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { useAuthStore } from '@/stores/authStore'

interface Post {
  id: string
  author: string
  authorId: string
  date: string
  content: string
  likes: string[]
  replies: Reply[]
}

interface Reply {
  id: string
  author: string
  authorId: string
  date: string
  content: string
}

const initialPosts: Post[] = [
  {
    id: 'post-001',
    author: '事務局（水谷恭子）',
    authorId: 'user-001',
    date: '11/26 18:31',
    content: '【腸内フローラバランス検査についてのお知らせ】\n日頃よりご協力を賜りありがとうございます。\n11/10の締切に続き、11/25締切分の受検体数が25件と規定数以下でしたので、検査会社の解析は持ち越しとなりました...',
    likes: ['user-002', 'user-003'],
    replies: [
      {
        id: 'reply-001',
        author: '吉岡 紘平',
        authorId: 'user-003',
        date: '11/26 19:00',
        content: '承知しました。次回の締切日を教えていただけますか？',
      }
    ],
  },
  {
    id: 'post-002',
    author: '田中三紀子',
    authorId: 'user-002',
    date: '11/19 9:54',
    content: '@移植医療機関(正会員)\n会員の先生方へ\n\nいつも大変お世話になっております。現在、高濃度水素NanoGAS水を移植前、若しくは移植の際に菌液注腸の直前に投与いただく取り組みを進めております...',
    likes: ['user-001'],
    replies: [],
  },
]

const members = [
  { id: 1, name: '清水（シンバイオシス研究所）', avatar: '清' },
  { id: 2, name: 'よろずクリニック', avatar: 'よ' },
  { id: 3, name: '医療法人感謝会', avatar: '医' },
  { id: 4, name: '城谷バイオウェルネス', avatar: '城' },
  { id: 5, name: '医療法人LAGOM', avatar: '医' },
]

export default function BoardSpace() {
  const { spaceId } = useParams()
  const { user } = useAuthStore()
  const [posts, setPosts] = useState<Post[]>(initialPosts)
  const [newPostContent, setNewPostContent] = useState('')
  const [replyingTo, setReplyingTo] = useState<string | null>(null)
  const [replyContent, setReplyContent] = useState('')
  const [notification, setNotification] = useState<{ type: 'success' | 'error', message: string } | null>(null)

  const spaceName = spaceId === 'regular-members' ? '掲示板（正会員）' : '掲示板'

  const showNotification = (type: 'success' | 'error', message: string) => {
    setNotification({ type, message })
    setTimeout(() => setNotification(null), 3000)
  }

  const handlePost = () => {
    if (!newPostContent.trim()) {
      showNotification('error', '投稿内容を入力してください')
      return
    }

    const newPost: Post = {
      id: `post-${Date.now()}`,
      author: user?.displayName || 'ゲスト',
      authorId: user?.id || 'guest',
      date: new Date().toLocaleDateString('ja-JP', { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' }),
      content: newPostContent,
      likes: [],
      replies: [],
    }

    setPosts([newPost, ...posts])
    setNewPostContent('')
    showNotification('success', '投稿しました')
  }

  const handleLike = (postId: string) => {
    setPosts(posts.map(post => {
      if (post.id === postId) {
        const userId = user?.id || 'guest'
        const hasLiked = post.likes.includes(userId)
        return {
          ...post,
          likes: hasLiked 
            ? post.likes.filter(id => id !== userId)
            : [...post.likes, userId]
        }
      }
      return post
    }))
  }

  const openReplyForm = (postId: string) => {
    setReplyingTo(postId)
    setReplyContent('')
  }

  const handleReply = (postId: string) => {
    if (!replyContent.trim()) {
      showNotification('error', '返信内容を入力してください')
      return
    }

    const newReply: Reply = {
      id: `reply-${Date.now()}`,
      author: user?.displayName || 'ゲスト',
      authorId: user?.id || 'guest',
      date: new Date().toLocaleDateString('ja-JP', { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' }),
      content: replyContent,
    }

    setPosts(posts.map(post => {
      if (post.id === postId) {
        return {
          ...post,
          replies: [...post.replies, newReply]
        }
      }
      return post
    }))

    setReplyingTo(null)
    setReplyContent('')
    showNotification('success', '返信しました')
  }

  const hasLiked = (post: Post) => {
    return post.likes.includes(user?.id || 'guest')
  }

  return (
    <>
      <header className="aip-main__header">
        <div className="aip-main__header-container">
          <h1 className="aip-main__header-title">{spaceName}</h1>
        </div>
      </header>

      <div className="aip-main__content">
        {/* 通知 */}
        {notification && (
          <div className={`notification notification--${notification.type}`}>
            <span className="material-symbols-outlined">
              {notification.type === 'success' ? 'check_circle' : 'error'}
            </span>
            {notification.message}
          </div>
        )}

        <div className="board-layout">
          {/* メインコンテンツ */}
          <div className="board-main">
            {/* 投稿フォーム */}
            <div className="board-post-form">
              <div className="board-post-form__header">
                <div className="board-post-form__avatar">
                  {user?.displayName?.charAt(0) || 'G'}
                </div>
                <span className="board-post-form__author">{user?.displayName || 'ゲスト'}</span>
              </div>
              <textarea
                className="board-post-form__input"
                placeholder="投稿する内容を入力..."
                rows={3}
                value={newPostContent}
                onChange={(e) => setNewPostContent(e.target.value)}
              />
              <div className="board-post-form__actions">
                <button 
                  onClick={handlePost} 
                  className="aip-button aip-button--solid-fill"
                  disabled={!newPostContent.trim()}
                >
                  <span className="material-symbols-outlined">send</span>
                  投稿
                </button>
              </div>
            </div>

            {/* 投稿一覧 */}
            <div className="board-posts">
              {posts.map((post) => (
                <div key={post.id} className="board-post">
                  <div className="board-post__header">
                    <div className="board-post__avatar">{post.author.charAt(0)}</div>
                    <div className="board-post__meta">
                      <span className="board-post__author">{post.author}</span>
                      <span className="board-post__date">{post.date}</span>
                    </div>
                  </div>
                  <div className="board-post__content">
                    {post.content.split('\n').map((line, i) => (
                      <p key={i}>{line}</p>
                    ))}
                  </div>
                  <div className="board-post__actions">
                    <button 
                      onClick={() => handleLike(post.id)} 
                      className={`board-post__action ${hasLiked(post) ? 'board-post__action--active' : ''}`}
                    >
                      <span className="material-symbols-outlined">
                        {hasLiked(post) ? 'thumb_up' : 'thumb_up'}
                      </span>
                      いいね！
                      {post.likes.length > 0 && <span className="board-post__action-count">{post.likes.length}</span>}
                    </button>
                    <button onClick={() => openReplyForm(post.id)} className="board-post__action">
                      <span className="material-symbols-outlined">reply</span>
                      返信
                      {post.replies.length > 0 && <span className="board-post__action-count">{post.replies.length}</span>}
                    </button>
                  </div>

                  {/* 返信一覧 */}
                  {post.replies.length > 0 && (
                    <div className="board-replies">
                      {post.replies.map((reply) => (
                        <div key={reply.id} className="board-reply">
                          <div className="board-reply__avatar">{reply.author.charAt(0)}</div>
                          <div className="board-reply__content">
                            <div className="board-reply__header">
                              <span className="board-reply__author">{reply.author}</span>
                              <span className="board-reply__date">{reply.date}</span>
                            </div>
                            <p>{reply.content}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* 返信フォーム */}
                  {replyingTo === post.id && (
                    <div className="board-reply-form">
                      <div className="board-reply-form__avatar">
                        {user?.displayName?.charAt(0) || 'G'}
                      </div>
                      <div className="board-reply-form__content">
                        <textarea
                          className="board-reply-form__input"
                          placeholder="返信を入力..."
                          rows={2}
                          value={replyContent}
                          onChange={(e) => setReplyContent(e.target.value)}
                          autoFocus
                        />
                        <div className="board-reply-form__actions">
                          <button 
                            onClick={() => setReplyingTo(null)} 
                            className="aip-button aip-button--outline aip-button--sm"
                          >
                            キャンセル
                          </button>
                          <button 
                            onClick={() => handleReply(post.id)} 
                            className="aip-button aip-button--solid-fill aip-button--sm"
                            disabled={!replyContent.trim()}
                          >
                            返信
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* サイドバー */}
          <aside className="board-sidebar">
            <div className="board-sidebar__section">
              <h3 className="board-sidebar__title">
                <span className="material-symbols-outlined">group</span>
                ピープル
              </h3>
              <div className="board-sidebar__subtitle">メンバー</div>
              <div className="board-members">
                {members.map((member) => (
                  <div key={member.id} className="board-member">
                    <div className="board-member__avatar">{member.avatar}</div>
                    <span className="board-member__name">{member.name}</span>
                  </div>
                ))}
                <button className="board-sidebar__more">さらに表示</button>
              </div>
            </div>
          </aside>
        </div>
      </div>

      <style>{`
        /* 通知 */
        .notification {
          display: flex;
          align-items: center;
          gap: var(--spacing-sm);
          padding: var(--spacing-md);
          border-radius: var(--border-radius-md);
          margin-bottom: var(--spacing-md);
          animation: slideIn 0.3s ease;
        }

        .notification--success {
          background-color: var(--color-success-light);
          color: var(--color-success);
        }

        .notification--error {
          background-color: var(--color-alert-light);
          color: var(--color-alert);
        }

        @keyframes slideIn {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .board-layout {
          display: grid;
          grid-template-columns: 1fr 280px;
          gap: var(--spacing-lg);
        }

        .board-main {
          display: flex;
          flex-direction: column;
          gap: var(--spacing-md);
        }

        .board-post-form {
          background-color: var(--background-primary);
          padding: var(--spacing-md);
          border-radius: var(--border-radius-lg);
          box-shadow: var(--shadow-sm);
        }

        .board-post-form__header {
          display: flex;
          align-items: center;
          gap: var(--spacing-sm);
          margin-bottom: var(--spacing-sm);
        }

        .board-post-form__avatar {
          width: 36px;
          height: 36px;
          border-radius: 50%;
          background-color: var(--color-blue500);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 600;
        }

        .board-post-form__author {
          font-weight: 500;
        }

        .board-post-form__input {
          width: 100%;
          padding: var(--spacing-sm);
          border: 1px solid var(--border-color);
          border-radius: var(--border-radius-md);
          resize: vertical;
          font-family: inherit;
        }

        .board-post-form__input:focus {
          outline: none;
          border-color: var(--color-blue500);
        }

        .board-post-form__actions {
          display: flex;
          justify-content: flex-end;
          margin-top: var(--spacing-sm);
        }

        .board-posts {
          display: flex;
          flex-direction: column;
          gap: var(--spacing-md);
        }

        .board-post {
          background-color: var(--background-primary);
          padding: var(--spacing-md);
          border-radius: var(--border-radius-lg);
          box-shadow: var(--shadow-sm);
        }

        .board-post__header {
          display: flex;
          gap: var(--spacing-sm);
          margin-bottom: var(--spacing-sm);
        }

        .board-post__avatar {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background-color: var(--color-blue500);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 600;
        }

        .board-post__meta {
          display: flex;
          flex-direction: column;
        }

        .board-post__author {
          font-weight: 500;
          color: var(--color-blue600);
        }

        .board-post__date {
          font-size: 0.75rem;
          color: var(--text-secondary);
        }

        .board-post__content {
          margin-bottom: var(--spacing-md);
          white-space: pre-wrap;
        }

        .board-post__content p {
          margin-bottom: var(--spacing-xs);
        }

        .board-post__actions {
          display: flex;
          gap: var(--spacing-md);
          padding-top: var(--spacing-sm);
          border-top: 1px solid var(--border-color);
        }

        .board-post__action {
          display: flex;
          align-items: center;
          gap: var(--spacing-xs);
          padding: var(--spacing-xs) var(--spacing-sm);
          border: none;
          background: none;
          color: var(--text-secondary);
          cursor: pointer;
          font-size: 0.875rem;
          border-radius: var(--border-radius-sm);
          transition: all 0.2s ease;
        }

        .board-post__action:hover {
          background-color: var(--color-solid-gray100);
          color: var(--color-blue600);
        }

        .board-post__action--active {
          color: var(--color-blue600);
        }

        .board-post__action--active .material-symbols-outlined {
          font-variation-settings: 'FILL' 1;
        }

        .board-post__action-count {
          font-weight: 500;
        }

        .board-post__action .material-symbols-outlined {
          font-size: 18px;
        }

        /* 返信一覧 */
        .board-replies {
          margin-top: var(--spacing-md);
          padding-left: var(--spacing-lg);
          border-left: 2px solid var(--color-solid-gray200);
        }

        .board-reply {
          display: flex;
          gap: var(--spacing-sm);
          padding: var(--spacing-sm) 0;
        }

        .board-reply__avatar {
          width: 28px;
          height: 28px;
          border-radius: 50%;
          background-color: var(--color-solid-gray300);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 0.75rem;
          font-weight: 600;
          flex-shrink: 0;
        }

        .board-reply__content {
          flex: 1;
        }

        .board-reply__header {
          display: flex;
          align-items: baseline;
          gap: var(--spacing-sm);
          margin-bottom: var(--spacing-xs);
        }

        .board-reply__author {
          font-size: 0.875rem;
          font-weight: 500;
          color: var(--color-blue600);
        }

        .board-reply__date {
          font-size: 0.6875rem;
          color: var(--text-secondary);
        }

        .board-reply__content p {
          font-size: 0.875rem;
        }

        /* 返信フォーム */
        .board-reply-form {
          display: flex;
          gap: var(--spacing-sm);
          margin-top: var(--spacing-md);
          padding-top: var(--spacing-md);
          border-top: 1px solid var(--border-color);
        }

        .board-reply-form__avatar {
          width: 28px;
          height: 28px;
          border-radius: 50%;
          background-color: var(--color-blue500);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 0.75rem;
          font-weight: 600;
          flex-shrink: 0;
        }

        .board-reply-form__content {
          flex: 1;
        }

        .board-reply-form__input {
          width: 100%;
          padding: var(--spacing-sm);
          border: 1px solid var(--border-color);
          border-radius: var(--border-radius-md);
          resize: none;
          font-family: inherit;
          font-size: 0.875rem;
        }

        .board-reply-form__input:focus {
          outline: none;
          border-color: var(--color-blue500);
        }

        .board-reply-form__actions {
          display: flex;
          justify-content: flex-end;
          gap: var(--spacing-sm);
          margin-top: var(--spacing-sm);
        }

        .aip-button--sm {
          padding: var(--spacing-xs) var(--spacing-sm);
          font-size: 0.75rem;
        }

        .board-sidebar {
          position: sticky;
          top: 80px;
        }

        .board-sidebar__section {
          background-color: var(--background-primary);
          padding: var(--spacing-md);
          border-radius: var(--border-radius-lg);
          box-shadow: var(--shadow-sm);
        }

        .board-sidebar__title {
          display: flex;
          align-items: center;
          gap: var(--spacing-xs);
          font-size: 0.875rem;
          font-weight: 600;
          margin-bottom: var(--spacing-md);
        }

        .board-sidebar__subtitle {
          font-size: 0.75rem;
          color: var(--text-secondary);
          margin-bottom: var(--spacing-sm);
        }

        .board-members {
          display: flex;
          flex-direction: column;
          gap: var(--spacing-xs);
        }

        .board-member {
          display: flex;
          align-items: center;
          gap: var(--spacing-sm);
        }

        .board-member__avatar {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          background-color: var(--color-solid-gray300);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 0.75rem;
        }

        .board-member__name {
          font-size: 0.75rem;
        }

        .board-sidebar__more {
          color: var(--color-blue600);
          font-size: 0.75rem;
          background: none;
          border: none;
          cursor: pointer;
          padding: var(--spacing-xs) 0;
        }

        @media (max-width: 1024px) {
          .board-layout {
            grid-template-columns: 1fr;
          }

          .board-sidebar {
            position: static;
          }
        }
      `}</style>
    </>
  )
}
